import { faTableList } from '@fortawesome/pro-solid-svg-icons'
import type { IntlShape } from 'react-intl'

import type { CombinedListingSearchResult } from '@/queries/CombinedListingSearchResult'

import {
  trackActionPanelActionClicked,
  trackViewManifest,
  type AnalyticsTracker,
} from '../../helpers/listViewAnalytics'
import type { ListViewSurface } from '../../helpers/listViewSurface'
import { PANEL_ACTION_ORDER } from '../panelActionOrder'
import type { PanelActionDefinition } from '../types'

const ns = 'Listings.ListView.actionPanel.viewManifest'

/**
 * Open the row's manifest full-screen without leaving the list.
 *
 * Always available, like Download manifest: the click loads the lot, then the
 * viewer opens already populated or the icon says there is no manifest. The
 * provider owns that sequence; this only tracks and gates.
 *
 * This runs for each row and we want it to be fast, so it has no hooks.
 */
export const createViewManifestPanelAction = ({
  formatMessage,
  surface,
  tracker,
  isSignedIn,
  openUnAuthModal,
  viewManifest,
}: {
  formatMessage: IntlShape['formatMessage']
  surface?: ListViewSurface
  tracker: AnalyticsTracker
  isSignedIn: boolean
  openUnAuthModal: (reason: string) => void
  viewManifest: (listing: CombinedListingSearchResult) => void
}): PanelActionDefinition => ({
  id: 'viewManifest',
  icon: faTableList,
  label: formatMessage({ id: `${ns}.label` }),
  description: formatMessage({ id: `${ns}.description` }),
  order: PANEL_ACTION_ORDER.viewManifest,
  onClick: (listing) => {
    // Track, then act — a guest's click is still a click.
    trackViewManifest(tracker, { listingId: listing.listingId, surface })
    trackActionPanelActionClicked(tracker, {
      action: 'view_manifest',
      listingId: listing.listingId,
      surface,
    })

    if (!isSignedIn) {
      openUnAuthModal(formatMessage({ id: `${ns}.signInReason` }))
      return
    }

    viewManifest(listing)
  },
})
